import { PLAN_CATALOG as SHARED_PLAN_CATALOG, PLAN_MAX_FILE_BYTES, YEARLY_DISCOUNT_MONTHS, maxFileBytesForPlan as sharedMaxFileBytesForPlan, type PlanCatalogEntry as SharedPlanCatalogEntry, type SubscriptionCycle } from '@kiao-voice/core'
import type { Plan } from './api-client'

export type PlanCatalogEntry = Plan & { maxFileBytes: number }

export { YEARLY_DISCOUNT_MONTHS }

/**
 * Web-side view of the shared plan catalog (`@kiao-voice/core`), which is also
 * what seeds the `subscription_plans` rows — so a price cannot move on one side
 * only. The reasoning behind each plan's numbers lives on that table.
 *
 * The DB is the RUNTIME source of truth: the pricing and billing pages fetch
 * live prices from `/plans`. This snapshot is only used where live data is not
 * available at render time — the offline FALLBACK on the marketing pricing page
 * (app/pricing/page.tsx), the route-local guest trial recommendation, and the
 * charged-amount math reported to analytics (`subscribeAmountTwd`).
 *
 * `maxFileBytes` is composed on here rather than carried by the shared table:
 * it is an upload-gate policy from `PLAN_MAX_FILE_BYTES`, not a column of
 * `subscription_plans`.
 */
export const PLAN_CATALOG: readonly PlanCatalogEntry[] = SHARED_PLAN_CATALOG.map(
  (plan: SharedPlanCatalogEntry) => ({
    code: plan.code,
    name: plan.name,
    monthlyPriceTwd: plan.monthlyPriceTwd,
    yearlyPriceTwd: plan.yearlyPriceTwd,
    monthlyCredits: plan.monthlyCredits,
    maxFileMinutes: plan.maxFileMinutes,
    maxFilesPerCycle: plan.maxFilesPerCycle,
    maxFileBytes: PLAN_MAX_FILE_BYTES[plan.code],
  })
)

/** Marketing cards receive live Plan records, while this policy remains fixed
 * in the upload gate, so their visible file-size line reads the same shared
 * table the server enforces. Unknown plans keep the Pro ceiling: this line is
 * marketing copy, and quoting a smaller number than a paying visitor actually
 * gets is the wrong way to be wrong. The upload modal, where the number gates
 * a real request, names the opposite fallback. */
export function maxFileBytesForPlan(planCode: Plan['code']): number {
  return sharedMaxFileBytesForPlan(planCode, { unknownFallback: PLAN_MAX_FILE_BYTES.pro })
}

/** Charged amount in TWD for a subscription plan + cycle. 0 for unknown plans. */
export function subscribeAmountTwd(planCode: string, cycle: SubscriptionCycle): number {
  const plan = PLAN_CATALOG.find((p) => p.code === planCode)
  if (!plan) return 0
  return cycle === 'yearly' ? plan.yearlyPriceTwd : plan.monthlyPriceTwd
}

/**
 * Whole-percent yearly discount vs paying monthly × 12, derived from the
 * actual prices so the "save X%" copy can never drift from what's charged.
 * Returns 0 for a free / zero-price plan.
 */
export function yearlyDiscountPct(monthlyPriceTwd: number, yearlyPriceTwd: number): number {
  if (monthlyPriceTwd <= 0) return 0
  return Math.round((1 - yearlyPriceTwd / (monthlyPriceTwd * 12)) * 100)
}
